import React, { useState, useEffect } from 'react';
import { useToast } from '../ui/Toast';
import { STORAGE_KEYS } from '../../constants';
import { useSettings } from '../../contexts/SettingsContext';
import './GeneralSettings.css';

/**
 * General Settings Component
 * Configuration for general admin dashboard settings
 */
const GeneralSettings = () => {
  const { showToast } = useToast();
  const settings = useSettings();
  const { updateSetting } = settings;
  
  // Sync local state with settings context
  const [autoRefresh, setAutoRefresh] = useState(settings.autoRefresh);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(settings.autoRefreshInterval);
  const [compactMode, setCompactMode] = useState(settings.compactMode);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(settings.sidebarCollapsed);
  const [tableItemsPerPage, setTableItemsPerPage] = useState(settings.tableItemsPerPage);
  const [showTooltips, setShowTooltips] = useState(settings.showTooltips);
  const [chartAnimations, setChartAnimations] = useState(settings.chartAnimations);
  const [dateFormat, setDateFormat] = useState(settings.dateFormat);
  const [currencyFormat, setCurrencyFormat] = useState(settings.currencyFormat);

  // Update local state when settings change from context
  useEffect(() => {
    setAutoRefresh(settings.autoRefresh);
    setAutoRefreshInterval(settings.autoRefreshInterval);
    setCompactMode(settings.compactMode);
    setSidebarCollapsed(settings.sidebarCollapsed);
    setTableItemsPerPage(settings.tableItemsPerPage);
    setShowTooltips(settings.showTooltips);
    setChartAnimations(settings.chartAnimations);
    setDateFormat(settings.dateFormat);
    setCurrencyFormat(settings.currencyFormat);
  }, [settings]);

  const handleAutoRefreshChange = (value) => {
    setAutoRefresh(value);
    updateSetting(STORAGE_KEYS.AUTO_REFRESH, value);
    showToast(`Auto-refresh ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handleAutoRefreshIntervalChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 10 && numValue <= 300) {
      setAutoRefreshInterval(numValue);
      updateSetting(STORAGE_KEYS.AUTO_REFRESH_INTERVAL, numValue);
      showToast(`Auto-refresh interval set to ${numValue} seconds`, 'success');
    }
  };

  const handleCompactModeChange = (value) => {
    setCompactMode(value);
    updateSetting(STORAGE_KEYS.COMPACT_MODE, value);
    showToast(`Compact mode ${value ? 'enabled' : 'disabled'}`, 'success');
    // No page reload needed - settings are reactive!
  };

  const handleSidebarCollapsedChange = (value) => {
    setSidebarCollapsed(value);
    updateSetting(STORAGE_KEYS.SIDEBAR_COLLAPSED, value);
    showToast('Sidebar preference saved', 'success');
    // No page reload needed - settings are reactive!
  };

  const handleTableItemsPerPageChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 5 && numValue <= 100) {
      setTableItemsPerPage(numValue);
      updateSetting(STORAGE_KEYS.TABLE_ITEMS_PER_PAGE, numValue);
      showToast(`Table items per page set to ${numValue}`, 'success');
    }
  };

  const handleShowTooltipsChange = (value) => {
    setShowTooltips(value);
    updateSetting(STORAGE_KEYS.SHOW_TOOLTIPS, value);
    showToast(`Tooltips ${value ? 'enabled' : 'disabled'}`, 'success');
    // Changes apply immediately via settings context!
  };

  const handleChartAnimationsChange = (value) => {
    setChartAnimations(value);
    updateSetting(STORAGE_KEYS.CHART_ANIMATIONS, value);
    showToast(`Chart animations ${value ? 'enabled' : 'disabled'}`, 'success');
    // Changes apply immediately via settings context!
  };

  const handleDateFormatChange = (value) => {
    setDateFormat(value);
    updateSetting(STORAGE_KEYS.DATE_FORMAT, value);
    showToast(`Date format set to ${value}`, 'success');
    // Changes apply immediately via settings context!
  };

  const handleCurrencyFormatChange = (value) => {
    setCurrencyFormat(value);
    updateSetting(STORAGE_KEYS.CURRENCY_FORMAT, value);
    showToast(`Currency format set to ${value}`, 'success');
    // Changes apply immediately via settings context!
  };

  const handleClearCache = () => {
    // Clear all cached analytics data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('admin-cache-')) {
        localStorage.removeItem(key);
      }
    });
    showToast('Cache cleared successfully', 'success');
    // No reload needed - settings are reactive!
  };

  return (
    <div className="general-settings admin-card">
      <h3 className="section-title">
        <i className="bi bi-gear icon-brand" style={{ marginRight: 8 }}></i>
        General Settings
      </h3>

      <div className="settings-group">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Auto-Refresh</h4>
            <p>Automatically refresh dashboard data</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => handleAutoRefreshChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {autoRefresh && (
          <div className="setting-item">
            <div className="setting-info">
              <h4>Auto-Refresh Interval</h4>
              <p>Refresh interval in seconds (10-300)</p>
            </div>
            <div className="input-group">
              <input
                type="number"
                min="10"
                max="300"
                value={autoRefreshInterval}
                onChange={(e) => handleAutoRefreshIntervalChange(e.target.value)}
                className="number-input"
              />
              <span className="input-suffix">seconds</span>
            </div>
          </div>
        )}

        <div className="setting-item">
          <div className="setting-info">
            <h4>Compact Mode</h4>
            <p>Use more compact layout for analytics and metrics</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={compactMode}
              onChange={(e) => handleCompactModeChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Collapsed Sidebar</h4>
            <p>Start with sidebar collapsed by default</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={sidebarCollapsed}
              onChange={(e) => handleSidebarCollapsedChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Table Items Per Page</h4>
            <p>Number of items to display per page in tables</p>
          </div>
          <div className="input-group">
            <input
              type="number"
              min="5"
              max="100"
              value={tableItemsPerPage}
              onChange={(e) => handleTableItemsPerPageChange(e.target.value)}
              className="number-input"
            />
            <span className="input-suffix">items</span>
          </div>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Show Tooltips</h4>
            <p>Display helpful tooltips on hover</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showTooltips}
              onChange={(e) => handleShowTooltipsChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Chart Animations</h4>
            <p>Enable animations in charts and graphs</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={chartAnimations}
              onChange={(e) => handleChartAnimationsChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Date Format</h4>
            <p>Preferred date display format</p>
          </div>
          <select
            value={dateFormat}
            onChange={(e) => handleDateFormatChange(e.target.value)}
            className="select-input"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            <option value="MMM DD, YYYY">MMM DD, YYYY</option>
            <option value="DD MMM YYYY">DD MMM YYYY</option>
          </select>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Currency Format</h4>
            <p>Preferred currency display</p>
          </div>
          <select
            value={currencyFormat}
            onChange={(e) => handleCurrencyFormatChange(e.target.value)}
            className="select-input"
          >
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
            <option value="JPY">JPY (¥)</option>
          </select>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="btn-secondary"
          onClick={handleClearCache}
        >
          <i className="bi bi-trash icon-brand"></i>
          Clear Cache
        </button>
      </div>
    </div>
  );
};

export default GeneralSettings;
